import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {TimeFormatsEnum} from '../../../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {onlyItem} from '../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {ReplyLocationsEnum} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {handlersWithTextInputIntent} from '../../../../../helpers/keyboardHandlerHelpers';
import {ReplyTypesEnum} from '../../../../../helpers/models/recipientHelpers';
import {ViewerDropdownInterfaceEventSourcesEnum} from '../../../../../helpers/models/tagHelpers';
import {areConversationSafetyNetsSatisfied} from '../../../../../helpers/safetyNetsHelpers';
import {ConversationBaseModel, ConversationModel} from '../../../../../models/api/conversationModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {SafetyNetsEnum} from '../../../../../models/api/safetyNetModel';
import {
  ExistingDraftError,
  ExistingDraftStrategiesEnum,
} from '../../../../../store/data/messages/draftMessagesActions';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {PermissionToolbarButton} from '../../../../common/buttons/permissionToolbarButton';
import {InboxesOfflineIndicator} from '../../../inboxesOfflineIndicator';
import {ViewerReplaceDraftDialog} from '../../dialogs/viewerReplaceDraftDialog';
import {
  ConversationsTagButtonProps,
  ConversationsTagCoordinator,
} from '../actions/tag/conversationsTagCoordinator';
import {ConversationTopbarAssign} from './conversationTopbarAssign';
import {ConversationTopbarInviteContainer} from './conversationTopbarInviteContainer';
import {ConversationTopbarMore} from './conversationTopbarMore';
import {ConversationTopbarMove} from './conversationTopbarMove';
import {ConversationTopbarSlaBreachIndicator} from './conversationTopbarSlaBreachIndicator';
import {ConversationTopbarTicket} from './conversationTopbarTicketId';

/*
 * Props.
 */

type ReplyHandler = (
  location: ReplyLocationsEnum,
  existingDraftStrategy: ExistingDraftStrategiesEnum,
  shouldPopout?: boolean,
) => void;
type CloneHandler = (location: ReplyLocationsEnum) => void;

interface ConversationTopbarProps {
  panelId: ViewerPanelId;
  teammate: TeammateModel;
  onlyConversation: ConversationModel | undefined;
  conversations: ReadonlyArray<ConversationBaseModel>;
  contextNamespaces: ReadonlyArray<string>;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  assigneeForAll: TeammateModel | undefined;
  reminderForAll: ReminderModel | undefined;
  areAllSubscribed: boolean;
  areAllShared: boolean;
  areAllInTeamContext: boolean;
  shouldShowSubscribe: boolean;
  isInPopup: boolean;
  replyType: ReplyTypesEnum;
  timeFormat: TimeFormatsEnum;
  isBulkActionRequired: boolean;
  isOffline: boolean;

  onReplyClick: ReplyHandler;
  onReplyAllClick: ReplyHandler;
  onReplyIntroClick: ReplyHandler;
  onForwardClick: ReplyHandler;
  onCloneClick: CloneHandler;
  onDeleteMessageClick: (arg: string) => void;
  onToggleAssignToSelfClick: () => void;
  onPopinClick: () => void;
  onSubscribeClick: () => void;
  onUnsubscribeClick: () => void;
  onSubjectChange: (arg: string) => void;
  timelineVariation?: TimelineVariationsEnum;
}

/*
 * Style.
 */

const StyledConversationTopbar = styled.div`
  grid-area: topbar;

  background-color: ${(p) => p.theme.appBackground};
`;

const StyledContentDiv = styled.div`
  grid-area: content;
  display: flex;
  align-items: center;
  padding: 8px 10px 0 8px;
  padding-left: 3px;
  padding-right: 14px;
`;

const StyledMiddleSpacer = styled.div`
  flex: 1;
`;

const StyledButtonDiv = styled.div``;

const StyledShrinkableButtonDiv = styled(StyledButtonDiv)`
  min-width: 0;
`;

const StyledButtonsDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-gap: 8px;
`;

const StyledOfflineIndicator = styled(InboxesOfflineIndicator)`
  padding: 5px;
`;

const StyledTagButtonWrapperDiv = styled.div`
  position: relative;
`;

const StyledStarDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};

  position: absolute;
  top: 3px;
  right: -1px;
  font-size: ${fontSizes.large};
`;

const StyledLeftButtonsWrapper = styled.div`
  ${flexCenter};
  /** Used to center the buttons with P2 buttons */
  margin-top: 2px;
`;

/*
 * Component.
 */

export const ConversationTopbar: FC<ConversationTopbarProps> = (props) => {
  const [onConfirmReplaceDraft, setOnConfirmReplaceDraft] = useState<(() => void) | undefined>(undefined);

  if (props.conversations.length === 0 || props.inboxesParameters.shouldHideTopbars) {
    return null;
  }

  // Used for handling the existing draft error for replies. This will first try
  // to execute the handler, and then catch ExistingDraftError's and show a warning
  // that the user is about to over-write their draft. If they confirm, the second handler
  // will then be called, which should have the existingDraftStrategy set to replace
  const withConfirmReplaceDraft = (
    handler: (arg: {existingDraftStrategy: ExistingDraftStrategiesEnum}) => void,
  ): void => {
    const existingDraftStrategy =
      props.timelineVariation === TimelineVariationsEnum.SYNCHRONOUS
        ? ExistingDraftStrategiesEnum.UPDATE_RECIPIENTS
        : ExistingDraftStrategiesEnum.THROW_ERROR;

    try {
      handler({existingDraftStrategy});
    } catch (error) {
      if (error instanceof ExistingDraftError) {
        setOnConfirmReplaceDraft(
          () => () => handler({existingDraftStrategy: ExistingDraftStrategiesEnum.REPLACE}),
        );
        return;
      }

      throw error;
    }
  };

  // Always bind keys for both actions.
  const keyHandlers = {
    ...handlersWithTextInputIntent({
      [KeyboardActionsEnum.REPLY]: () =>
        withConfirmReplaceDraft(({existingDraftStrategy}) =>
          props.onReplyClick(ReplyLocationsEnum.KEYBOARD, existingDraftStrategy),
        ),
      [KeyboardActionsEnum.REPLY_IN_NEW_WINDOW]: () =>
        withConfirmReplaceDraft(({existingDraftStrategy}) =>
          props.onReplyClick(ReplyLocationsEnum.KEYBOARD, existingDraftStrategy, true),
        ),
      [KeyboardActionsEnum.REPLY_ALL]: () =>
        withConfirmReplaceDraft(({existingDraftStrategy}) =>
          props.onReplyAllClick(ReplyLocationsEnum.KEYBOARD, existingDraftStrategy),
        ),
      [KeyboardActionsEnum.REPLY_ALL_IN_NEW_WINDOW]: () =>
        withConfirmReplaceDraft(({existingDraftStrategy}) =>
          props.onReplyAllClick(ReplyLocationsEnum.KEYBOARD, existingDraftStrategy, true),
        ),
      [KeyboardActionsEnum.REPLY_INTRO]: () =>
        withConfirmReplaceDraft(({existingDraftStrategy}) =>
          props.onReplyIntroClick(ReplyLocationsEnum.KEYBOARD, existingDraftStrategy),
        ),
      [KeyboardActionsEnum.FORWARD]: () =>
        withConfirmReplaceDraft(({existingDraftStrategy}) =>
          props.onForwardClick(ReplyLocationsEnum.KEYBOARD, existingDraftStrategy),
        ),
      [KeyboardActionsEnum.CLONE]: () => props.onCloneClick(ReplyLocationsEnum.KEYBOARD),
    }),
    [KeyboardActionsEnum.ASSIGN_TO_SELF]: () => props.onToggleAssignToSelfClick(),
    [KeyboardActionsEnum.SUBSCRIBE]: () =>
      props.areAllSubscribed ? props.onUnsubscribeClick() : props.onSubscribeClick(),
  };

  const onlyConversation = onlyItem(props.conversations);

  const maybeRenderRequiredTaggingStar = () => {
    if (
      !onlyConversation ||
      areConversationSafetyNetsSatisfied(onlyConversation, SafetyNetsEnum.REQUIRED_TAGGING)
    ) {
      return null;
    }

    return <StyledStarDiv>*</StyledStarDiv>;
  };

  return (
    <StyledConversationTopbar>
      <KeyboardShortcuts handlers={keyHandlers} />
      <StyledContentDiv>
        <StyledLeftButtonsWrapper>
          <StyledButtonDiv>
            <ConversationTopbarMove
              cellsContext={props.cellsContext}
              panelId={props.panelId}
              conversations={props.conversations}
              inboxesParameters={props.inboxesParameters}
              isBulkActionRequired={props.isBulkActionRequired}
            />
          </StyledButtonDiv>
          {props.onlyConversation && <ConversationTopbarTicket conversation={props.onlyConversation} />}
          {props.onlyConversation && (
            <ConversationTopbarSlaBreachIndicator conversation={props.onlyConversation} />
          )}
        </StyledLeftButtonsWrapper>
        <StyledMiddleSpacer />
        <StyledButtonsDiv>
          <StyledOfflineIndicator
            isOffline={props.isOffline}
            renderTooltip={() => (
              <Trans id="viewer-conversation-offline-tooltip">
                You will see the latest activity when you&apos;re back online
              </Trans>
            )}
          />
          <ConversationTopbarMore
            cellsContext={props.cellsContext}
            inboxesParameters={props.inboxesParameters}
            onlyConversation={props.onlyConversation}
            conversations={props.conversations}
            isBulkActionRequired={props.isBulkActionRequired}
            panelId={props.panelId}
          />
          <StyledTagButtonWrapperDiv>
            <ConversationsTagCoordinator
              key={props.conversations.map((conversation) => conversation.id).join(' ')}
              cellsContext={props.cellsContext}
              inboxesParameters={props.inboxesParameters}
              onlyConversation={props.onlyConversation}
              conversations={props.conversations}
              isBulkActionRequired={props.isBulkActionRequired}
              panelId={props.panelId}
              renderButton={(tagProps) => renderAddTagsButton(tagProps)}
              interfaceEventSource={ViewerDropdownInterfaceEventSourcesEnum.TOPBAR}
            />
            {maybeRenderRequiredTaggingStar()}
          </StyledTagButtonWrapperDiv>
          {renderInviteMenu(props)}
          {renderAssign(props)}
        </StyledButtonsDiv>
      </StyledContentDiv>
      <ViewerReplaceDraftDialog
        onConfirmReplaceDraft={onConfirmReplaceDraft}
        onRequestClose={() => setOnConfirmReplaceDraft(undefined)}
      />
    </StyledConversationTopbar>
  );
};

/*
 * Helpers.
 */

function renderAddTagsButton(props: ConversationsTagButtonProps) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <PermissionToolbarButton
          iconName="tag"
          label={<Trans id="conversation-tags">Add tags</Trans>}
          hasHover={props.hasHover}
          keyboardAction={KeyboardActionsEnum.TAG}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
          bulkActionPermission={props.permissionResult}
          noMargin
        />
      )}
    </UserThemeConsumer>
  );
}

/*
 * Invite.
 */

function renderInviteMenu(props: ConversationTopbarProps) {
  const {onlyConversation} = props;

  if (!onlyConversation) {
    return null;
  }

  return (
    <ConversationTopbarInviteContainer cellsContext={props.cellsContext} conversation={onlyConversation} />
  );
}

/*
 * Assign.
 */

function renderAssign(props: ConversationTopbarProps) {
  const StyledWrapper = props.assigneeForAll ? StyledShrinkableButtonDiv : StyledButtonDiv;

  return (
    <StyledWrapper>
      <ConversationTopbarAssign
        cellsContext={props.cellsContext}
        conversations={props.conversations}
        assigneeAll={props.assigneeForAll}
        panelId={props.panelId}
        inboxesParameters={props.inboxesParameters}
        isBulkActionRequired={props.isBulkActionRequired}
      />
    </StyledWrapper>
  );
}
